/**
 * Debtors page — list/add/edit Sundry Debtors, view per-client ledger,
 * orders, custom price list, and execute lumpsum FIFO settlements.
 *
 * iter200 refactor: form modal, ledger drawer, and lumpsum modal extracted
 * into ../components/debtors/* to keep this file <400 lines and isolate the
 * heavier render blocks behind clear prop boundaries.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useAdminDelete } from '../hooks/useAdminDelete';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import {
  Plus, Search, BookOpen, Trash2, Edit2, Send, Users, TrendingUp, ArrowLeftRight,
} from 'lucide-react';
import DebtorFormModal from '../components/debtors/DebtorFormModal';
import DebtorLedgerDrawer from '../components/debtors/DebtorLedgerDrawer';
import SettlementEditor from '../components/ledger/SettlementEditor';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const EMPTY_FORM = {
  name: '', type: 'Debtor', gstin: '', address: '', city: '', state: 'Maharashtra',
  pin_code: '', phone: '', email: '', payment_terms: 'Net 30', credit_limit: '',
  opening_balance: '', opening_balance_type: 'Dr', opening_balance_as_of: '',
  opening_balance_note: '',
  // Default dispatch / reference details — auto-filled into Enterprise &
  // GST bill forms so the operator doesn't re-type them every time.
  default_dispatch_address: '', default_transport_mode: '',
  default_transporter_name: '', default_vehicle_no: '',
  default_eway_bill_required: false, default_reference_prefix: '',
  default_payment_terms: '',
  notes: '',
};

const EMPTY_PRICE_ROW = { product_id: '', product_name: '', item_type: 'finished', unit: 'KG', rate: '', discount_pct: '', markup_pct: '', notes: '' };

export default function Debtors() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';

  // Core list state
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [outstandingOnly, setOutstandingOnly] = useState(false);

  // Add/Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  // Ledger drawer
  const [ledgerClient, setLedgerClient] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerTab, setLedgerTab] = useState('ledger');
  const [stmtSending, setStmtSending] = useState(false);
  const [clientOrders, setClientOrders] = useState([]);

  // Price list (lives inside the drawer's pricelist tab)
  const [products, setProducts] = useState([]);
  const [priceList, setPriceList] = useState([]);
  const [priceListSaving, setPriceListSaving] = useState(false);
  const [newPriceRow, setNewPriceRow] = useState(EMPTY_PRICE_ROW);

  // Lumpsum modal
  const [showLumpsum, setShowLumpsum] = useState(false);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/clients/`, { withCredentials: true });
      setClients(res.data.filter(c => c.type === 'Debtor' || c.type === 'Both'));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const { deleteState, requestDelete, confirmDelete, cancelDelete } = useAdminDelete(fetchData);

  useEffect(() => { fetchData(); }, []);

  // FG + RM products for the price list — merged, each tagged with item_type
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/inventory/finished-goods`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/inventory/raw-materials`,  { withCredentials: true }).catch(() => ({ data: [] })),
    ]).then(([fgRes, rmRes]) => {
      const fgList = (fgRes.data || []).map(p => ({ ...p, item_type: 'finished' }));
      const rmList = (rmRes.data || []).map(p => ({
        ...p,
        item_type: 'raw',
        // RMs use `current_rate` — surface as price_per_unit for the pre-fill logic
        price_per_unit: p.current_rate || p.price_per_unit || 0,
      }));
      setProducts([...fgList, ...rmList]);
    }).catch(console.error);
  }, []);

  const openAdd  = () => { setEditing(null); setForm({ ...EMPTY_FORM, type: 'Debtor' }); setShowModal(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY_FORM, ...c }); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      if (editing) {
        const res = await axios.put(`${API}/clients/${editing.id}`, form, { withCredentials: true });
        setClients(prev => prev.map(c => c.id === editing.id ? res.data : c).filter(c => c.type === 'Debtor' || c.type === 'Both'));
      } else {
        const res = await axios.post(`${API}/clients/`, form, { withCredentials: true });
        if (res.data.type === 'Debtor' || res.data.type === 'Both') {
          setClients(prev => [...prev, res.data]);
        }
      }
      setShowModal(false);
      toast.success(editing ? 'Debtor updated!' : 'Debtor added!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error saving client'); }
    finally { setSubmitting(false); }
  };

  const viewLedger = async (client) => {
    setLedgerClient(client);
    setLedgerTab('ledger');
    setLedgerLoading(true);
    try {
      const [ledgerRes, priceRes, ordersRes] = await Promise.all([
        axios.get(`${API}/clients/${client.id}/ledger`, { withCredentials: true }),
        axios.get(`${API}/clients/${client.id}/price-list`, { withCredentials: true }),
        axios.get(`${API}/sales/orders?include_history=true`, { withCredentials: true }),
      ]);
      setLedger(ledgerRes.data);
      setPriceList(priceRes.data || []);
      setClientOrders((ordersRes.data || []).filter(o => o.client === client.name));
    } catch (e) { toast.error('Error loading client data'); }
    finally { setLedgerLoading(false); }
  };

  const addPriceRow = () => {
    if (!newPriceRow.product_id && !newPriceRow.product_name) return;
    const p = products.find(pr => pr.id === newPriceRow.product_id);
    const entry = {
      ...newPriceRow,
      product_name: p?.name || newPriceRow.product_name,
      item_type: p?.item_type || newPriceRow.item_type || 'finished',
      rate: parseFloat(newPriceRow.rate) || 0,
      discount_pct: parseFloat(newPriceRow.discount_pct) || 0,
      markup_pct: parseFloat(newPriceRow.markup_pct) || 0,
    };
    setPriceList(prev => [...prev, entry]);
    setNewPriceRow(EMPTY_PRICE_ROW);
  };

  const removePriceRow = (idx) => setPriceList(prev => prev.filter((_, i) => i !== idx));
  const updatePriceRow = (idx, field, value) =>
    setPriceList(prev => prev.map((r, i) => i !== idx ? r : { ...r, [field]: value }));

  const savePriceListSuccess = async () => {
    if (!ledgerClient) return;
    setPriceListSaving(true);
    try {
      await axios.put(`${API}/clients/${ledgerClient.id}/price-list`, { price_list: priceList }, { withCredentials: true });
      toast.success('Price list saved!');
    } catch (e) { toast.error('Error saving price list'); }
    finally { setPriceListSaving(false); }
  };

  const sendStatement = async (client) => {
    if (!client.email) { toast.error('Client has no email address'); return; }
    setStmtSending(true);
    try {
      await axios.post(`${API}/clients/${client.id}/send-statement`, {}, { withCredentials: true });
      toast.success(`Statement sent to ${client.email}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send statement'); }
    finally { setStmtSending(false); }
  };

  const filtered = clients.filter(c => {
    if (outstandingOnly && !(parseFloat(c.balance || 0) > 0)) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(s)
      || (c.city || '').toLowerCase().includes(s)
      || (c.state || '').toLowerCase().includes(s)
      || (c.gstin || '').toLowerCase().includes(s)
      || (c.phone || '').toLowerCase().includes(s)
      || (c.email || '').toLowerCase().includes(s)
      || (c.contact_person || '').toLowerCase().includes(s);
  });

  if (loading) return <div className="text-zinc-400 text-sm animate-pulse">Loading debtors...</div>;

  return (
    <div className="space-y-5" data-testid="debtors-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">Sundry Debtors</h1>
          <p className="text-zinc-500 text-sm mt-0.5">Customers who owe you money — {clients.length} debtors</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="add-debtor-btn" onClick={openAdd}
            className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-2 rounded-xl transition-colors">
            <Plus size={14} /> New Debtor
          </button>
          {isAdmin && (
            <button data-testid="realloc-fifo-debtors-btn" onClick={async () => {
              if (!window.confirm('Reallocate ALL debtor lumpsum receipts strictly against the OLDEST invoices first (opening balance → oldest invoice → newer)?\n\nSafe & idempotent — already-correct settlements are untouched.')) return;
              try {
                const res = await axios.post(`${API}/invoices/reallocate-fifo`, {}, { withCredentials: true });
                const { debtors_touched, total_applied } = res.data || {};
                toast.success(`FIFO reallocation done · ${debtors_touched} debtor(s) touched · ₹${(total_applied || 0).toLocaleString()} re-applied`);
                await fetchData();
              } catch (e) { toast.error(e.response?.data?.detail || 'Reallocation failed'); }
            }}
              title="Re-run oldest-invoice-first settlement across every debtor. Idempotent."
              className="flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-amber-400 font-semibold text-xs px-3 py-2 rounded-xl border border-amber-700/40 transition-colors">
              <ArrowLeftRight size={14} /> Reallocate FIFO
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          { label: 'Total Debtors',     value: clients.length,                              icon: Users,       color: 'text-blue-400',  bg: 'bg-blue-500/10' },
          { label: 'With Credit Limit', value: clients.filter(c => c.credit_limit > 0).length, icon: TrendingUp,  color: 'text-amber-400', bg: 'bg-amber-500/10' },
          { label: 'With Email',        value: clients.filter(c => c.email).length,         icon: Send,        color: 'text-green-400', bg: 'bg-green-500/10' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
              <Icon size={16} className={color} />
            </div>
            <div>
              <div className="text-zinc-400 text-xs">{label}</div>
              <div className="text-zinc-50 font-heading font-bold text-xl">{value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Search + filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-xs flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            placeholder="Search by name, city, GSTIN, phone, email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-xl pl-9 pr-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500"
            data-testid="debtor-search"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer select-none" data-testid="outstanding-only-toggle">
          <input type="checkbox" checked={outstandingOnly} onChange={e => setOutstandingOnly(e.target.checked)}
            className="w-3.5 h-3.5 accent-amber-500" />
          Outstanding only
        </label>
        <span className="text-zinc-600 text-xs font-mono">{filtered.length}/{clients.length}</span>
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="border-b border-zinc-800">
              {['Debtor Name', 'GSTIN', 'City', 'Phone', 'Payment Terms', 'Credit Limit', 'Actions'].map(h => (
                <th key={h} className="text-left text-zinc-500 text-xs font-medium px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => (
              <tr key={c.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors" data-testid={`debtor-row-${c.id}`}>
                <td className="px-4 py-3">
                  <button onClick={() => viewLedger(c)} className="text-amber-400 hover:text-amber-300 font-medium text-xs transition-colors">
                    {c.name}
                  </button>
                  {c.type === 'Both' && <span className="ml-2 text-[10px] bg-amber-900/30 text-amber-400 px-1.5 py-0.5 rounded-full">Also Creditor</span>}
                </td>
                <td className="px-4 py-3 text-zinc-400 text-xs font-mono">{c.gstin || '—'}</td>
                <td className="px-4 py-3 text-zinc-400 text-xs">{c.city || '—'}</td>
                <td className="px-4 py-3 text-zinc-400 text-xs">{c.phone || '—'}</td>
                <td className="px-4 py-3 text-zinc-300 text-xs">{c.payment_terms || '—'}</td>
                <td className="px-4 py-3 text-zinc-300 text-xs font-mono">{c.credit_limit ? fmt(c.credit_limit) : '—'}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => viewLedger(c)} title="View Ledger"
                      className="text-zinc-500 hover:text-amber-400 transition-colors" data-testid={`ledger-btn-${c.id}`}>
                      <BookOpen size={13} />
                    </button>
                    <button onClick={() => openEdit(c)} title="Modify all fields"
                      data-testid={`modify-debtor-btn-${c.id}`}
                      className="flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-900/30 text-blue-300 hover:bg-blue-800/50 border border-blue-700/40 text-[11px] font-semibold transition-colors">
                      <Edit2 size={11} /> Modify
                    </button>
                    {isAdmin && <button onClick={() => requestDelete(`/clients/${c.id}`, c.name, `Delete debtor "${c.name}" and all linked sales orders & dispatches.`)} title="Delete"
                      className="p-1 rounded-lg bg-red-900/30 text-red-400 hover:text-red-300 border border-red-800/30">
                      <Trash2 size={13} />
                    </button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center text-zinc-500 text-sm py-10">
            {search ? 'No debtors match your search' : 'No debtors yet — add your first debtor'}
          </div>
        )}
      </div>

      <DebtorFormModal
        open={showModal}
        editing={editing}
        form={form}
        setForm={setForm}
        submitting={submitting}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmit}
      />

      {ledgerClient && (
        <DebtorLedgerDrawer
          client={ledgerClient}
          ledger={ledger}
          ledgerLoading={ledgerLoading}
          ledgerTab={ledgerTab}
          setLedgerTab={setLedgerTab}
          stmtSending={stmtSending}
          onSendStatement={sendStatement}
          onEditProfile={openEdit}
          onClose={() => { setLedgerClient(null); setLedger(null); setPriceList([]); }}
          onLumpsum={() => setShowLumpsum(true)}
          clientOrders={clientOrders}
          products={products}
          priceList={priceList}
          newPriceRow={newPriceRow}
          setNewPriceRow={setNewPriceRow}
          addPriceRow={addPriceRow}
          removePriceRow={removePriceRow}
          updatePriceRow={updatePriceRow}
          priceListSaving={priceListSaving}
          onSavePriceList={savePriceListSuccess}
          onRefreshLedger={() => ledgerClient && viewLedger(ledgerClient)}
          canEditLedger={['admin', 'owner', 'manager', 'accountant'].includes(user?.role)}
        />
      )}

      {ledgerClient && (
        <SettlementEditor
          open={showLumpsum}
          clientId={ledgerClient.id}
          clientName={ledgerClient.name}
          side="debtor"
          onClose={() => setShowLumpsum(false)}
          onSaved={() => ledgerClient && viewLedger(ledgerClient)}
        />
      )}

      <ConfirmDeleteModal open={deleteState.open} onClose={cancelDelete} onConfirm={confirmDelete} title={deleteState.title} message={deleteState.message} />
    </div>
  );
}
